<template>
  <div class="product__greeting-card" v-if="productGrettingCardVariants.length && !greetingCardDisable">
    <div class="greeting-card__title">
      <template v-if="dynamicPrice && title">
        {{ parsedInfo.title }}
        <span v-show="dynamicTotalPrice"
          >(+ {{ formatPrice(dynamicTotalPrice) }} USD)</span
        >
      </template>
      <template v-else-if="title">{{ parsedInfo.title }}</template>
      <template v-else>
        Choose your greeting card (+
        {{ formatPrice(firstVariant?.price) }} USD)
        <span
          class="greeting-card__tag-sales"
          v-if="firstVariant?.compareAtPrice && percentDiscount"
          >Sale {{ percentDiscount }}%
        </span>
        <del
          class="greeting-card__compare-price"
          v-if="firstVariant?.compareAtPrice"
          >{{ formatPrice(firstVariant?.compareAtPrice) }} USD</del
        >
      </template>
    </div>
    <template v-if="multipleSelection">
      <div class="greeting-card__items">
        <div
          class="greeting-card__item"
          :class="{
            'greeting-card__item--active': indexChoosed == index,
            'greeting-card__item--active-selected':
              indexChoosedArr.includes(index),
          }"
          v-for="(item, index) of productGrettingCardVariants"
          :key="index"
          @click="selecteMultipleProductVariant(index)"
        >
          <div class="greeting-card__item--image">
            <template v-if="customItems && customItems.length > 0">
              <img :src="item?.image" />
            </template>
            <sib-image v-else v-bind="getImageProductVariant(item)"> </sib-image>

            <div class="checkbox">
              <label
                :for="item.id + index"
                class="checkbox-choose_greeting-card"
              >
                <input
                  class="checkbox-choose_greeting-card__input"
                  type="checkbox"
                  :id="item.id + index"
                  :checked="indexChoosedArr.includes(index)"
                />
                <svg
                  class="checkbox-choose_greeting-card__icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 22 22"
                >
                  <rect
                    width="21"
                    height="21"
                    x=".5"
                    y=".5"
                    fill="#FFF"
                    stroke="#38ba72"
                    rx="3"
                  />
                  <path
                    class="tick"
                    stroke="#38ba72"
                    fill="none"
                    stroke-linecap="round"
                    stroke-width="4"
                    d="M4 10l5 5 9-9"
                  />
                </svg>
              </label>
            </div>
          </div>
        </div>
        <div
          v-if="showNoOption"
          class="greeting-card__item greeting-card__item--no-thanks"
          :class="{
            'greeting-card__item--active-no-thanks': indexChoosedArr.includes(
              productGrettingCardVariants.length
            ),
          }"
          @click="
            selecteMultipleProductVariant(productGrettingCardVariants.length)
          "
        >
          No, Thanks
        </div>
      </div>
      <div v-if="showUpsellTable" class="greeting-card-discount">
        <div class="content">
          <div class="discount-line">
            <div>Choose 2 items</div>
            <div>
              <span class="price-final">$5.99</span
              ><span class="price-compare ml-xs">$7.98</span>
            </div>
          </div>
          <div class="discount-line">
            <div>Choose 3 items</div>
            <div>
              <span class="price-final">$7.99</span
              ><span class="price-compare ml-xs">$11.97</span>
            </div>
          </div>
          <div class="discount-line">
            <div>Choose 5 items</div>
            <div>
              <span class="price-final">$9.99</span
              ><span class="price-compare ml-xs">$19.95</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="greeting-card__items">
        <div
          class="greeting-card__item"
          :class="{
            'greeting-card__item--active': indexChoosed == index,
          }"
          v-for="(item, index) of productGrettingCardVariants"
          :key="index"
          @click="selecteProductVariant(index)"
        >
          <sib-image v-bind="getImageProductVariant(item)"></sib-image>
        </div>
        <div
          v-if="showNoOption"
          class="greeting-card__item greeting-card__item--no-thanks"
          :class="{
            'greeting-card__item--active-no-thanks':
              indexChoosed == productGrettingCardVariants.length,
          }"
          @click="selecteProductVariant(productGrettingCardVariants.length)"
        >
          No, Thanks
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import { NullOrType, Product } from '@/types'
import { computed, onMounted, ref, watch } from 'vue'
import { QUERY_STRING_NO_CACHE } from '@/utils'
import { formatPrice } from '@/utils/product'
import { useRoute } from 'vue-router'
import useRequestLocationStore from '@/store/requestLocation'

interface Props {
  handle?: string
  title: string
  multipleSelection?: boolean
  dynamicPrice?: boolean
  showUpsellTable?: boolean
  showNoOption?: boolean
  customItems?: any[]
  selectedIndex?: number
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  handle: '',
  multipleSelection: true,
  dynamicPrice: false,
  showUpsellTable: false,
  showNoOption: false,
  customItems: () => [],
  selectedIndex: -1
})

const emit = defineEmits<{
  (e: 'greeting-card-selected', value: any): void
  (e: 'item-selected', value: any): void
}>()

const route = useRoute()
const productStore = useProductStore()
const requestLocation = useRequestLocationStore()

const product = ref<NullOrType<Product>>(null)
const indexChoosed = ref(-1)
const variantSelected = ref<any>(null)
const indexChoosedArr = ref<number[]>([])
const variantSelectedArr = ref<any[]>([])
const firstVariant = ref<any>(null)
const greetingCardDisable = ref(false)

const productGrettingCardVariants = computed(() => {
  if (props.customItems && props.customItems.length > 0) {
    return props.customItems
  }
  return product.value?.variants || []
})

const parsedInfo = computed(() => {
  const result = {
    title: props.title || 'Buy greeting cards',
    price: ''
  }

  if (props.title) {
    const priceRegex = /\(\+\s?\$([\d\.]+)\s?USD\)/i
    const match = props.title.match(priceRegex)
    
    if (match) {
      result.price = `+$${match[1]}`
      result.title = props.title.replace(match[0], '').trim()
    }
  }

  return result
})

const percentDiscount = computed(() => {
  if (!firstVariant.value || !firstVariant.value.compareAtPrice) return 0
  return Math.round(
    ((firstVariant.value.compareAtPrice - firstVariant.value.price) /
      firstVariant.value.compareAtPrice) *
      100
  )
})

const dynamicTotalPrice = computed(() => {
  if (props.multipleSelection) {
    return variantSelectedArr.value.length
      ? variantSelectedArr.value.reduce(
          (sum, item) => sum + (item?.price || 0),
          0
        )
      : 0
  }
  return variantSelected.value?.price || 0
})

onMounted(() => {
  if (!props.customItems || props.customItems.length === 0) {
    getProductByHandle()
  }
})

watch(
  () => variantSelected.value,
  () => {
    emit('greeting-card-selected', {
      greetingCardVariantSelected: variantSelected.value,
      quantity: 1,
      productHandle: props.handle || '',
    })
  }
)

watch(() => variantSelectedArr.value, (val) => {
  if (props.customItems && props.customItems.length > 0) return
  emit('greeting-card-selected', {
    greetingCardVariantArrSelected: val || [],
    productHandle: props.handle || '',
    multiple: props.multipleSelection,
  })
}, { deep: true })

watch(() => props.selectedIndex, (val) => {
  if (props.customItems && props.customItems.length > 0) {
    indexChoosedArr.value = val !== undefined && val !== -1 ? [val] : []
  }
}, { immediate: true })

async function getProductByHandle() {
  if (!props.handle) return
  const isForceLoadNewData = !!route.query[QUERY_STRING_NO_CACHE]
  try {
    product.value = await productStore.getProductByHandle({
      handle: props.handle,
      isForceLoadNewData,
    })
    if (product.value?.variants?.[0]) {
      firstVariant.value = product.value?.variants[0]
    }

    greetingCardDisable.value = !!(product.value?.handle.includes('ipatugrc00424') &&
      requestLocation.countryCode && requestLocation.countryCode.toUpperCase() != 'US')
    
    indexChoosedArr.value = props.showNoOption ? [product.value?.variants?.length || 0] : []
    variantSelectedArr.value = []

    if (props.handle.includes('ipatugrc00424')) {
      selecteProductVariant(0)
    }
  } catch (error) {}
}

function selecteProductVariant(index: number) {
  indexChoosed.value = index
  if (!productGrettingCardVariants.value?.length) return
  if (
    indexChoosed.value == productGrettingCardVariants.value?.length ||
    indexChoosed.value == -1
  ) {
    variantSelected.value = null
    indexChoosedArr.value = [productGrettingCardVariants.value?.length]
  } else {
    variantSelected.value = productGrettingCardVariants.value[index]
    indexChoosedArr.value = [index]
  }
}

function selecteMultipleProductVariant(index: number) {
  if (props.customItems && props.customItems.length > 0) {
    indexChoosedArr.value = [index]
    emit('item-selected', { index, item: props.customItems[index] })
    return
  }

  if (!props.multipleSelection) {
    selecteProductVariant(index)
    return
  }

  if (!productGrettingCardVariants.value?.length) return

  const indexNoChoose = indexChoosedArr.value.findIndex(
    (item) => item == productGrettingCardVariants.value?.length
  )

  if (index == productGrettingCardVariants.value?.length) {
    indexChoosed.value = index
    variantSelectedArr.value = []

    if (indexNoChoose == -1) {
      indexChoosedArr.value.push(productGrettingCardVariants.value?.length)
    }

    indexChoosedArr.value = indexChoosedArr.value.filter(
      (item) => item == productGrettingCardVariants.value?.length
    )
    variantSelectedArr.value = []
  } else {
    const indexOfIndexChoosedArr = indexChoosedArr.value.findIndex(
      (item) => item == index
    )
    const indexOfVariantSelectedArr = variantSelectedArr.value.findIndex(
      (item) => item.id == productGrettingCardVariants.value[index].id
    )
    if (indexChoosed.value != index) {
      indexChoosed.value = index

      if (indexNoChoose > -1) indexChoosedArr.value.splice(indexNoChoose, 1)

      if (indexOfIndexChoosedArr == -1) {
        indexChoosedArr.value.push(index)
      }

      if (indexOfVariantSelectedArr == -1) {
        variantSelectedArr.value.push(productGrettingCardVariants.value[index])
      }
    } else {
      indexChoosed.value = index

      if (indexOfIndexChoosedArr > -1) {
        indexChoosedArr.value.splice(indexOfIndexChoosedArr, 1)
      } else {
        indexChoosedArr.value.push(index)
      }

      if (indexOfVariantSelectedArr > -1) {
        variantSelectedArr.value.splice(indexOfVariantSelectedArr, 1)
      } else {
        variantSelectedArr.value.push(productGrettingCardVariants.value[index])
      }
    }
  }
}

function getImageProductVariant(productVariant: any) {
  return product.value?.images?.find(
    (item) => item.id == productVariant?.image?.id
  ) || { src: '', alt: '', width: 1000, height: 1000 }
}
</script>

<style lang="scss">
.product__greeting-card {
  background: var(--lavender);
  padding: 0 1rem 2rem 1rem;
  .greeting-card {
    &__items {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(62px, 1fr));
      gap: 9px;
      margin: 5px;
    }
    &__item {
      background-color: transparent;
      box-sizing: border-box;
      box-shadow: 0 0 2px 1px #0003;
      border: 3px solid #fff;
      width: 62px;
      height: 62px;
      transition: all 0.3s ease;
      cursor: pointer;
      &--active {
        border-color: #2f80ed;
        width: 204px;
        height: 204px;
        grid-column: span 3;
        grid-row: span 3;
      }
      &--active-no-thanks,
      &--active-selected {
        border-color: #2f80ed;
      }
      &--no-thanks {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-weight: 600;
      }
      &--active,
      &--active-selected,
      &--active-no-thanks {
        position: relative;
        &::before {
          content: '';
          position: absolute;
          width: 12px;
          height: 12px;
          right: 2px;
          bottom: 0;
          background: url('@/assets/images/icons/check-mark.svg');
          background-repeat: no-repeat;
          background-size: cover;
          z-index: var(--base-z-index);
        }
      }
      &--image {
        position: relative;
        .checkbox {
          position: absolute;
          bottom: 6px;
          right: 0;
          z-index: var(--base-z-index);
        }
      }
    }
    &__compare-price {
      font-size: 12px;
      color: var(--product-compare-at-price-color);
      margin-left: 5px;
    }
    &__title {
      font-size: 1em;
      font-weight: 600;
      margin-bottom: 1rem;
      margin-left: 4px;
    }
    &__tag-sales {
      color: #ffffff;
      font-size: 10px;
      vertical-align: middle;
      font-weight: 600;
      background: var(--color-secondary);
      border-radius: 4px;
      white-space: nowrap;
      padding: 3px 8px 4px 8px;
    }
  }
}

.checkbox-choose_greeting-card {
  $block: &;

  cursor: pointer;
  display: flex;
  align-items: center;

  &__input {
    position: absolute;
    width: 1rem;
    height: 1rem;
    opacity: 0;
    cursor: pointer;

    &:checked + #{$block}__icon .tick {
      stroke-dashoffset: 0;
    }
  }

  &__icon {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
    overflow: visible;

    .tick {
      stroke-dasharray: 20px;
      stroke-dashoffset: 20px;
      transition: stroke-dashoffset 0.2s ease-out;
    }
  }
}
.greeting-card-discount {
  font-size: 13px;
  margin-top: 1rem;
  display: grid;
  grid-template-columns: 1fr;
  max-width: 400px;
  .discount-line {
    padding: 0.3em 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-bottom: solid 1px var(--border-color);
  }
  @include media-md-up {
    .discount-line {
      grid-template-columns: 100px 1fr;
    }
  }
  @include media-lg-up {
    .discount-line {
      grid-template-columns: 150px 1fr;
    }
  }
  @include media-xl-up {
    .discount-line {
      grid-template-columns: 200px 1fr;
    }
  }
  .price-final {
    font-weight: 600;
  }
  .price-compare {
    text-decoration: line-through;
    color: var(--text-color-tertiary);
  }
}
</style>
